import { useLayoutEffect, useRef, useState } from 'react'

import {
  EVENT_NAME_ORIENTATION_CHANGE,
  EVENT_NAME_RESIZE,
} from '../../../../../constants/event'
import { useAnimationFrame } from '../../../../../hooks'

import { MIN_VISIBLE_COUNT } from './useMultiValuedListResponsiveVisibleCount.constants'
import {
  getResponsiveVisibleCount,
  readFlexGap,
} from './useMultiValuedListResponsiveVisibleCount.utils'

export function useMultiValuedListResponsiveVisibleCount({
  enabled,
  items,
  minVisibleCount = MIN_VISIBLE_COUNT,
}) {
  const [visibleCount, setVisibleCount] = useState(items.length)

  const containerRef = useRef(null)
  const measurementRef = useRef(null)
  const moreMeasureRef = useRef(null)

  const hasItems = items.length > 0

  function handleMeasure() {
    if (enabled && containerRef.current && measurementRef.current) {
      const itemsContainer = measurementRef.current.firstElementChild

      if (itemsContainer) {
        const itemWidths = Array.from(itemsContainer.children).map(
          node => node.offsetWidth
        )

        const nextVisibleCount = getResponsiveVisibleCount({
          containerWidth: containerRef.current.clientWidth,
          itemsGap: readFlexGap(itemsContainer),
          itemWidths,
          minVisibleCount,
          moreWidth: moreMeasureRef.current?.offsetWidth ?? 0,
          rootGap: readFlexGap(measurementRef.current),
        })

        setVisibleCount(previousValue => {
          return previousValue === nextVisibleCount
            ? previousValue
            : nextVisibleCount
        })
      }
    }
  }

  const [scheduleMeasure, cancelMeasure] = useAnimationFrame(handleMeasure)

  useLayoutEffect(() => {
    if (enabled && hasItems && containerRef.current) {
      const observer = new ResizeObserver(scheduleMeasure)

      observer.observe(containerRef.current)

      return function cleanup() {
        observer.disconnect()
      }
    }
  }, [enabled, hasItems, scheduleMeasure])

  useLayoutEffect(() => {
    if (enabled) {
      handleMeasure()
    }
  }, [enabled, items])

  useLayoutEffect(() => {
    if (enabled) {
      window.addEventListener(EVENT_NAME_RESIZE, scheduleMeasure)
      window.addEventListener(EVENT_NAME_ORIENTATION_CHANGE, scheduleMeasure)
      window.visualViewport?.addEventListener(
        EVENT_NAME_RESIZE,
        scheduleMeasure
      )

      return function cleanup() {
        window.removeEventListener(EVENT_NAME_RESIZE, scheduleMeasure)
        window.removeEventListener(
          EVENT_NAME_ORIENTATION_CHANGE,
          scheduleMeasure
        )
        window.visualViewport?.removeEventListener(
          EVENT_NAME_RESIZE,
          scheduleMeasure
        )
      }
    }
  }, [enabled])

  useLayoutEffect(() => {
    return function cleanup() {
      cancelMeasure()
    }
  }, [])

  return {
    containerRef,
    measurementRef,
    moreMeasureRef,
    visibleCount,
  }
}
